import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useComboboxTypeahead } from "@/hooks/useComboboxTypeahead";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type GeoOption = {
  id: number;
  name: string;
};

type GeoSearchSelectProps = {
  value: string;
  options: GeoOption[];
  onChange: (id: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
};

function itemValue(id: string): string {
  return `geo:${id}`;
}

export function GeoSearchSelect({
  value,
  options,
  onChange,
  placeholder = "Select",
  searchPlaceholder = "Search…",
  emptyText = "No results.",
  disabled = false,
}: GeoSearchSelectProps) {
  const { open, setOpen, onTriggerKeyDown, onOpenChange, commandInputProps } =
    useComboboxTypeahead(disabled);
  const [highlight, setHighlight] = useState("");

  const selected = useMemo(
    () => options.find((option) => String(option.id) === value) ?? null,
    [options, value],
  );

  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>("[data-geo-option-selected='true']")
        ?.scrollIntoView({ block: "nearest" });
    });
    return () => cancelAnimationFrame(frame);
  }, [open, value]);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (next) setHighlight(value ? itemValue(value) : "");
      }}
      modal
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          onKeyDown={onTriggerKeyDown}
          className={cn(
            "glass-input h-10 w-full justify-between px-3 font-normal text-sm",
            !selected && "text-muted-foreground",
          )}
        >
          <span className="truncate">{selected ? selected.name : placeholder}</span>
          <ChevronsUpDown className="ml-1.5 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 z-[240]" align="start" sideOffset={4}>
        <Command
          value={highlight}
          onValueChange={setHighlight}
          filter={(item, search, keywords) => {
            const q = search.trim().toLowerCase();
            if (!q) return 1;
            const haystack = `${item} ${(keywords ?? []).join(" ")}`.toLowerCase();
            return haystack.includes(q) ? 1 : 0;
          }}
        >
          <CommandInput {...commandInputProps} placeholder={searchPlaceholder} className="h-9 text-xs" />
          <CommandList className="max-h-[240px] overflow-y-auto">
            <CommandEmpty className="py-2.5 text-center text-xs text-muted-foreground">{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const id = String(option.id);
                const isSelected = id === value;
                return (
                  <CommandItem
                    key={id}
                    value={itemValue(id)}
                    keywords={[option.name]}
                    data-geo-option-selected={isSelected ? "true" : undefined}
                    onSelect={() => {
                      onChange(id);
                      setOpen(false);
                    }}
                    className="text-xs flex items-center justify-between cursor-pointer"
                  >
                    <span className="break-words font-medium">{option.name}</span>
                    <Check
                      className={cn(
                        "ml-2 h-3.5 w-3.5 shrink-0 text-primary",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
